import { getParentPageName } from '@/lib/utils';
import { InternalLinkInterface } from '@/types';

/**
 * Builds a URL based on the provided internal link data.
 *
 * @param internalLinkData - The data for the internal link
 * @returns A string representing the constructed URL
 */
const urlBuilder = (internalLinkData: InternalLinkInterface): string => {
  // Return home if no slug or language is provided
  if (!internalLinkData?.slug?.current || !internalLinkData.language) return '/';

  const baseUrl: InternalLinkInterface = {
    slug: { current: internalLinkData.slug.current },
    language: internalLinkData.language,
  };

  let formattedLink: InternalLinkInterface;

  // Handle different page types
  if (internalLinkData._type === 'page') {
    const parentSlug = internalLinkData.pageParent?.slug?.current;
    const grandParentSlug = internalLinkData.pageParent?.pageParent?.slug?.current;

    formattedLink = parentSlug
      ? {
          ...baseUrl,
          pageParent: {
            slug: { current: parentSlug },
            ...(grandParentSlug && {
              pageParent: {
                slug: { current: grandParentSlug },
              },
            }),
          },
        }
      : baseUrl;
  } else if (internalLinkData._type === 'event' || internalLinkData._type === 'exhibit') {
    formattedLink = {
      ...baseUrl,
      pageParent: {
        slug: { current: getParentPageName(internalLinkData._type) },
        pageParent: { slug: { current: 'explore' } },
      },
    };
  } else if (internalLinkData._type && internalLinkData._type !== 'page') {
    formattedLink = {
      ...baseUrl,
      pageParent: {
        slug: { current: getParentPageName(internalLinkData._type) },
        ...((internalLinkData._type === 'pressRelease' || internalLinkData._type === 'person') && {
          pageParent: { slug: { current: 'about' } },
        }),
      },
    };
  } else {
    return '/'; // No valid page type
  }

  // Destructure for URL construction
  const { slug, pageParent, language } = formattedLink;

  // Handle homepage slug
  if (slug.current === 'homepage') {
    slug.current = '';
  }

  // Build URL path
  const parentPath = pageParent ? `/${pageParent.slug.current}` : '';
  const parentsParentPath = pageParent?.pageParent?.slug.current
    ? `/${pageParent?.pageParent?.slug.current}`
    : '';

  // Construct and return the final URL
  if (language !== 'en') {
    // For non-English languages, include the language code in the URL
    return `/${language}${parentsParentPath}${parentPath}/${slug.current}`;
  }

  // For English, omit the language code
  return `${parentsParentPath}${parentPath}/${slug.current}`;
};

export default urlBuilder;
